import React, { useMemo } from "react";
import { formatCurrency } from "utils/helperFunctions";
import CostBreakdownModal from "components/financial/CostBreakdownModal";
import ProductInput from "components/financial/ProductInput";
import PlatformMonthlyPricing from "components/financial/PlatformMonthlyPricing";
import { useGetTwilioCostQuery, useGetIntelliSmsCostQuery } from "apis/financial";
import { useGetExternalApiCostSummaryQuery } from "apis/externalApi";
import { useGetAyrshareUsageQuery } from "apis/ayrshare";
import { useGetProductTagsQuery } from "apis/productTag";
import { useGetRegistrySearchBreakdownQuery } from "apis/registrySearch";

/**
 * Drill-down modals for the non-AI providers. Each one maps its own response
 * shape onto the shared CostBreakdownModal tree.
 *
 * Cost columns are removed — costs are now manually entered per month, so
 * calculated cost breakdowns per org/purpose are not meaningful. Only usage
 * metrics (messages, segments, calls, posts) are shown.
 */

const num = (n) => (n ?? 0).toLocaleString("en-AU");

const PURPOSE_LABELS = {
  appraisal: "Appraisal",
  comparableMatching: "Comparable matching",
  addressLookup: "Address lookup",
  priceEstimate: "Price estimate",
  propertyReport: "Property report",
  goLive: "Go live",
  listingStatus: "Listing status",
  campaignStats: "Campaign stats",
  suburbSync: "Suburb sync",
  enquiryPoll: "Enquiry poll",
  agentAssistant: "Sandy (agent assistant)",
  unlabelled: "Unlabelled",
};
const prettyPurpose = (p) => PURPOSE_LABELS[p] || p || "—";

/* ── Twilio / IntelliSMS: organization → property → purpose ─────────────── */

const twilioCols = [
  { key: "messages", header: "Messages", align: "right" },
  { key: "segments", header: "Segments", align: "right" },
  { key: "charged", header: "Charged", align: "right" },
];

export const TwilioCostBreakdown = ({
  isOpen,
  onClose,
  title = "Twilio — SMS Usage",
  provider = "twilio",
  dateParams = {},
  monthlyPrices = {},
  dateValue = {},
  onSetMonthlyPrice,
  onUpsertProduct,
}) => {
  const isIntelliSms = provider === "intellisms";
  const { data: twilioData } = useGetTwilioCostQuery(dateParams, isOpen && !isIntelliSms);
  const { data: intelliData } = useGetIntelliSmsCostQuery(dateParams, isOpen && isIntelliSms);
  const data = isIntelliSms ? intelliData : twilioData;
  const { data: productTags } = useGetProductTagsQuery("smsPurpose", isOpen);

  const totalSegs = data?.totalSegments ?? data?.totalMessages ?? 0;

  const nodes = useMemo(
    () =>
      (data?.organizations ?? []).map((org, oi) => ({
        id: org.organizationId || `org-${oi}`,
        label: org.organizationName,
        muted: !org.organizationId,
        sublabel: org.hasSubscription ? "Has subscription" : null,
        values: {
          messages: num(org.totalMessages),
          segments: num(org.totalSegments ?? org.totalMessages),
          charged: num(org.chargedCount),
        },
        children: (org.propertyBreakdown ?? []).map((prop, pi) => ({
          id: `${org.organizationId || oi}-${prop.propertyId || pi}`,
          label: prop.address || "No property",
          muted: !prop.propertyId,
          values: {
            messages: num(prop.totalMessages),
            segments: num(prop.totalSegments ?? prop.totalMessages),
            charged: num(prop.chargedCount),
          },
          children: (prop.purposeBreakdown ?? []).map((p, i) => ({
            id: `${org.organizationId || oi}-${prop.propertyId || pi}-${p.purpose}-${i}`,
            label: p.purpose,
            values: {
              messages: num(p.count),
              segments: num(p.segments ?? p.count),
              charged: num(p.chargedCount),
            },
          })),
        })),
      })),
    [data],
  );

  const purposeNodes = useMemo(() => {
    const byPurpose = new Map();
    for (const org of data?.organizations ?? []) {
      for (const prop of org.propertyBreakdown ?? []) {
        for (const p of prop.purposeBreakdown ?? []) {
          if (!byPurpose.has(p.purpose)) byPurpose.set(p.purpose, { total: 0, segments: 0, orgs: new Map() });
          const entry = byPurpose.get(p.purpose);
          entry.total += p.count;
          entry.segments += p.segments ?? p.count;
          const key = org.organizationName;
          const prev = entry.orgs.get(key) ?? { count: 0, segments: 0 };
          entry.orgs.set(key, {
            count: prev.count + p.count,
            segments: prev.segments + (p.segments ?? p.count),
          });
        }
      }
    }
    return [...byPurpose.entries()]
      .map(([purpose, e]) => ({
        id: purpose,
        label: purpose,
        values: {
          messages: num(e.total),
          segments: num(e.segments),
        },
        _n: e.segments,
        children: [...e.orgs.entries()]
          .sort((a, b) => b[1].segments - a[1].segments)
          .map(([orgName, v]) => ({
            id: `${purpose}-${orgName}`,
            label: orgName,
            values: { messages: num(v.count), segments: num(v.segments) },
          })),
      }))
      .sort((a, b) => b._n - a._n);
  }, [data]);

  const monthlyPricingSlot = (
    <PlatformMonthlyPricing
      provider={provider}
      monthlyPrices={monthlyPrices}
      dateValue={dateValue}
      onSetMonthlyPrice={onSetMonthlyPrice}
    />
  );

  return (
    <CostBreakdownModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="Organization → property → purpose"
      summary={[
        { label: "Messages", value: num(data?.totalMessages) },
        { label: "Segments", value: num(data?.totalSegments ?? data?.totalMessages) },
        { label: "Charged", value: num(data?.totalCharged) },
        { label: "Paid", value: num(data?.totalPaid) },
      ]}
      headerSlot={monthlyPricingSlot}
      views={[
        {
          key: "org",
          label: "By organization",
          levels: [
            { header: "Organization", columns: twilioCols },
            { header: "Property", columns: twilioCols },
            { header: "Purpose", columns: twilioCols },
          ],
          nodes,
          emptyText: "No SMS usage recorded for this period.",
        },
        {
          key: "purpose",
          label: "By purpose",
          levels: [
            {
              header: "Purpose",
              columns: [
                {
                  key: "product",
                  header: "Product",
                  align: "left",
                  render: (node) => {
                    const saved = (productTags ?? []).find(
                      (p) => p.keyType === "smsPurpose" && p.key === node.id
                    );
                    return (
                      <ProductInput
                        key={`${node.id}-${saved?.product ?? ""}`}
                        defaultValue={saved?.product ?? ""}
                        onSave={(product) => onUpsertProduct?.({ id: saved?._id, keyType: "smsPurpose", key: node.id, product })}
                      />
                    );
                  },
                },
                ...twilioCols,
              ],
            },
            { header: "Organization", columns: twilioCols },
          ],
          nodes: purposeNodes,
          emptyText: "No purpose breakdown available.",
          searchPlaceholder: "Search purposes…",
        },
      ]}
    />
  );
};

/* ── Domain: organization → purpose ──────────────────────────────────────── */

const domainCols = [
  { key: "calls", header: "Billable", align: "right" },
  { key: "failed", header: "Failed", align: "right" },
];

export const DomainCostBreakdown = ({
  isOpen,
  onClose,
  rangeParams = {},
  monthlyPrices = {},
  dateValue = {},
  onSetMonthlyPrice,
  onUpsertProduct,
}) => {
  const { data } = useGetExternalApiCostSummaryQuery(
    { provider: "domain", ...rangeParams },
    isOpen,
  );
  const { data: productTags } = useGetProductTagsQuery("domainPurpose", isOpen);

  const total = data?.totalBillableCalls ?? 0;

  const nodes = useMemo(() => {
    const orgs = [...(data?.organizations ?? [])];
    if (data?.platformOverhead?.calls > 0) orgs.push(data.platformOverhead);
    return orgs.map((org, i) => ({
      id: org.organizationId || `overhead-${i}`,
      label: org.organizationName,
      muted: org.isPlatformOverhead,
      badge: org.isPlatformOverhead ? "no customer" : null,
      values: {
        calls: num(org.billableCalls),
        failed: num(org.failedCalls),
      },
      children: (org.purposeBreakdown ?? []).map((b, j) => ({
        id: `${org.organizationId || i}-${b.purpose}-${j}`,
        label: prettyPurpose(b.purpose),
        sublabel: b.billingPackage,
        values: {
          calls: num(b.billableCalls),
          failed: num(b.failedCalls),
        },
      })),
    }));
  }, [data, total]);

  const purposeNodes = useMemo(() => {
    const orgs = [...(data?.organizations ?? [])];
    if (data?.platformOverhead?.calls > 0) orgs.push(data.platformOverhead);
    const byPurpose = new Map();
    for (const org of orgs) {
      for (const b of org.purposeBreakdown ?? []) {
        if (!byPurpose.has(b.purpose)) {
          byPurpose.set(b.purpose, { calls: 0, failed: 0, orgs: [] });
        }
        const e = byPurpose.get(b.purpose);
        e.calls += b.billableCalls ?? 0;
        e.failed += b.failedCalls ?? 0;
        e.orgs.push({ org, b });
      }
    }
    return [...byPurpose.entries()]
      .map(([purpose, e]) => ({
        id: purpose,
        label: prettyPurpose(purpose),
        values: {
          calls: num(e.calls),
          failed: num(e.failed),
        },
        _n: e.calls,
        children: e.orgs
          .sort((x, y) => (y.b.billableCalls ?? 0) - (x.b.billableCalls ?? 0))
          .map(({ org, b }, i) => ({
            id: `${purpose}-${org.organizationId || i}`,
            label: org.organizationName,
            muted: org.isPlatformOverhead,
            values: {
              calls: num(b.billableCalls),
              failed: num(b.failedCalls),
            },
          })),
      }))
      .sort((a, b) => b._n - a._n);
  }, [data, total]);

  const monthlyPricingSlot = (
    <PlatformMonthlyPricing
      provider="domain"
      monthlyPrices={monthlyPrices}
      dateValue={dateValue}
      onSetMonthlyPrice={onSetMonthlyPrice}
    />
  );

  return (
    <CostBreakdownModal
      isOpen={isOpen}
      onClose={onClose}
      title="Domain — API Usage"
      subtitle="Organization → what the calls were for"
      summary={[
        { label: "Billable calls", value: num(data?.totalBillableCalls) },
        { label: "Failed", value: num(data?.totalFailedCalls), tone: "warn" },
      ]}
      headerSlot={monthlyPricingSlot}
      views={[
        {
          key: "org",
          label: "By organization",
          levels: [
            { header: "Organization", columns: domainCols },
            { header: "Used for", columns: domainCols },
          ],
          nodes,
          emptyText: "No Domain API usage recorded for this period.",
        },
        {
          key: "purpose",
          label: "By purpose",
          levels: [
            {
              header: "Used for",
              columns: [
                {
                  key: "product",
                  header: "Product",
                  align: "left",
                  render: (node) => {
                    const saved = (productTags ?? []).find(
                      (p) => p.keyType === "domainPurpose" && p.key === node.id,
                    );
                    return (
                      <ProductInput
                        key={`${node.id}-${saved?.product ?? ""}`}
                        defaultValue={saved?.product ?? ""}
                        onSave={(product) =>
                          onUpsertProduct?.({ id: saved?._id, keyType: "domainPurpose", key: node.id, product })
                        }
                      />
                    );
                  },
                },
                ...domainCols,
              ],
            },
            { header: "Organization", columns: domainCols },
          ],
          nodes: purposeNodes,
          emptyText: "No purpose breakdown available.",
          searchPlaceholder: "Search purposes…",
        },
      ]}
    />
  );
};

/* ── Ayrshare: organization → platform → agent → post type ───────────────── */

const ayrCols = [
  { key: "posts", header: "Posts", align: "right" },
  { key: "agents", header: "Agents", align: "right" },
];

export const AyrshareCostBreakdown = ({
  isOpen,
  onClose,
  rangeParams = {},
  monthlyPrices = {},
  dateValue = {},
  onSetMonthlyPrice,
  onUpsertProduct,
}) => {
  const { data } = useGetAyrshareUsageQuery(
    { tree: true, ...rangeParams },
    isOpen,
  );
  const { data: productTags } = useGetProductTagsQuery("ayrsharePlatform", isOpen);

  const total = data?.totalPosts ?? 0;

  const nodes = useMemo(
    () =>
      (data?.organizations ?? []).map((org, oi) => ({
        id: org.organizationId || `ayr-${oi}`,
        label: org.organizationName,
        muted: !org.organizationId,
        values: {
          posts: num(org.totalPosts),
          agents: num(org.agentsWithProfile),
        },
        children: (org.platforms ?? []).map((plat) => ({
          id: `${org.organizationId || oi}-${plat.platform}`,
          label: plat.platform,
          sublabel:
            plat.agentsWithNoPosts > 0
              ? `${plat.agentsWithNoPosts} paid profile${plat.agentsWithNoPosts === 1 ? "" : "s"} unused`
              : null,
          values: {
            posts: num(plat.totalPosts),
            agents: num(plat.agentCount),
          },
          children: (plat.agents ?? []).map((agent) => ({
            id: `${org.organizationId || oi}-${plat.platform}-${agent.userId}`,
            label: agent.name,
            sublabel: agent.email || null,
            badge: agent.totalPosts === 0 ? "unused" : null,
            muted: agent.totalPosts === 0,
            values: { posts: num(agent.totalPosts) },
            children: (agent.postTypes ?? []).map((t) => ({
              id: `${org.organizationId || oi}-${plat.platform}-${agent.userId}-${t.postType}`,
              label: t.postType,
              values: { posts: num(t.totalPosts) },
            })),
          })),
        })),
      })),
    [data, total],
  );

  const platformNodes = useMemo(() => {
    const byPlatform = new Map();
    for (const org of data?.organizations ?? []) {
      for (const plat of org.platforms ?? []) {
        if (!byPlatform.has(plat.platform)) {
          byPlatform.set(plat.platform, { posts: 0, unused: 0, orgs: [] });
        }
        const e = byPlatform.get(plat.platform);
        e.posts += plat.totalPosts ?? 0;
        e.unused += plat.agentsWithNoPosts ?? 0;
        e.orgs.push({ org, plat });
      }
    }
    return [...byPlatform.entries()]
      .map(([platform, e]) => ({
        id: platform,
        label: platform,
        sublabel:
          e.unused > 0
            ? `${e.unused} paid profile${e.unused === 1 ? "" : "s"} unused`
            : null,
        values: {
          posts: num(e.posts),
        },
        _n: e.posts,
        children: e.orgs
          .sort((x, y) => (y.plat.totalPosts ?? 0) - (x.plat.totalPosts ?? 0))
          .map(({ org, plat }, i) => ({
            id: `${platform}-${org.organizationId || i}`,
            label: org.organizationName,
            values: {
              posts: num(plat.totalPosts),
              agents: num(plat.agentCount),
            },
            children: (plat.agents ?? []).map((agent) => ({
              id: `${platform}-${org.organizationId || i}-${agent.userId}`,
              label: agent.name,
              badge: agent.totalPosts === 0 ? "unused" : null,
              muted: agent.totalPosts === 0,
              values: { posts: num(agent.totalPosts) },
            })),
          })),
      }))
      .sort((a, b) => b._n - a._n);
  }, [data, total]);

  const monthlyPricingSlot = (
    <PlatformMonthlyPricing
      provider="ayrshare"
      monthlyPrices={monthlyPrices}
      dateValue={dateValue}
      onSetMonthlyPrice={onSetMonthlyPrice}
    />
  );

  return (
    <CostBreakdownModal
      isOpen={isOpen}
      onClose={onClose}
      title="Ayrshare — Posting Usage"
      subtitle="Organization → platform → agent → post type"
      summary={[
        { label: "Posts", value: num(data?.totalPosts) },
        { label: "Organizations", value: num(data?.organizationCount) },
        { label: "Paid profiles", value: num(data?.agentsWithProfile) },
        {
          label: "Unused profiles",
          value: num(data?.agentsWithProfileAndNoPosts),
          tone: "warn",
        },
      ]}
      headerSlot={monthlyPricingSlot}
      note="Ayrshare bills a flat per-profile subscription, not per post."
      views={[
        {
          key: "org",
          label: "By organization",
          levels: [
            { header: "Organization", columns: ayrCols },
            { header: "Platform", columns: ayrCols },
            { header: "Agent", columns: ayrCols },
            { header: "Post type", columns: ayrCols },
          ],
          nodes,
          emptyText: "No Ayrshare posting recorded for this period.",
        },
        {
          key: "platform",
          label: "By platform",
          levels: [
            {
              header: "Platform",
              columns: [
                {
                  key: "product",
                  header: "Product",
                  align: "left",
                  render: (node) => {
                    const saved = (productTags ?? []).find(
                      (p) => p.keyType === "ayrsharePlatform" && p.key === node.id,
                    );
                    return (
                      <ProductInput
                        key={`${node.id}-${saved?.product ?? ""}`}
                        defaultValue={saved?.product ?? ""}
                        onSave={(product) =>
                          onUpsertProduct?.({ id: saved?._id, keyType: "ayrsharePlatform", key: node.id, product })
                        }
                      />
                    );
                  },
                },
                ...ayrCols,
              ],
            },
            { header: "Organization", columns: ayrCols },
            { header: "Agent", columns: ayrCols },
          ],
          nodes: platformNodes,
          emptyText: "No platform breakdown available.",
          searchPlaceholder: "Search platforms…",
        },
      ]}
    />
  );
};

/* ── Xero: supplier → invoices (DISABLED) ───────────────────────────────── */

const xeroCols = [
  { key: "date", header: "Date", align: "left" },
  { key: "status", header: "Status", align: "left" },
  { key: "amount", header: "Amount", align: "right", strong: true },
];

export const XeroCostBreakdown = ({ isOpen, onClose, providers, otherBills, grandTotal }) => {
  const nodes = useMemo(() => {
    const rows = Object.entries(providers || {}).map(([key, p]) => ({
      id: key,
      label: (p.invoices?.[0]?.contactName) || key,
      sublabel: `${p.invoiceCount} invoice${p.invoiceCount === 1 ? "" : "s"}`,
      values: {
        date: "",
        status: p.amountDue > 0 ? `${formatCurrency(p.amountDue)} due` : "Paid",
        amount: formatCurrency(p.totalAmount),
      },
      children: (p.invoices ?? []).map((inv) => ({
        id: inv.invoiceId,
        label: inv.invoiceNumber || inv.invoiceId,
        sublabel: inv.contactName,
        values: {
          date: inv.date ? String(inv.date).slice(0, 10) : "—",
          status: inv.status,
          amount: formatCurrency(inv.total),
        },
      })),
    }));
    if (otherBills?.length) {
      rows.push({
        id: "__other__",
        label: "Unmapped suppliers",
        muted: true,
        sublabel: `${otherBills.length} bill${otherBills.length === 1 ? "" : "s"} not matched to a platform`,
        values: {
          date: "",
          status: "",
          amount: formatCurrency(
            otherBills.reduce((s, b) => s + (b.total ?? 0), 0),
          ),
        },
        children: otherBills.map((inv) => ({
          id: inv.invoiceId,
          label: inv.contactName || inv.invoiceNumber,
          sublabel: inv.invoiceNumber,
          values: {
            date: inv.date ? String(inv.date).slice(0, 10) : "—",
            status: inv.status,
            amount: formatCurrency(inv.total),
          },
        })),
      });
    }
    return rows;
  }, [providers, otherBills]);

  return (
    <CostBreakdownModal
      isOpen={isOpen}
      onClose={onClose}
      title="Xero — Bills"
      subtitle="Supplier → invoice"
      summary={[
        { label: "Suppliers", value: num(nodes.length) },
        { label: "Total billed", value: formatCurrency(grandTotal ?? 0) },
      ]}
      searchPlaceholder="Search suppliers…"
      levels={[
        { header: "Supplier", columns: xeroCols },
        { header: "Invoice", columns: xeroCols },
      ]}
      nodes={nodes}
      emptyText="No Xero bills for this period."
    />
  );
};

/* ── Registry Search: network → agent → property ─────────────────────────── */

const registryCols = [
  { key: "searches", header: "Searches", align: "right" },
];

export const RegistrySearchBreakdown = ({
  isOpen,
  onClose,
  provider,
  title,
  rangeParams = {},
  monthlyPrices = {},
  dateValue = {},
  onSetMonthlyPrice,
}) => {
  const { data } = useGetRegistrySearchBreakdownQuery(
    { provider, ...rangeParams },
    isOpen,
  );

  const totalSearches = useMemo(
    () => (data?.networks ?? []).reduce((s, n) => s + (n.searches ?? 0), 0),
    [data],
  );

  const nodes = useMemo(
    () =>
      (data?.networks ?? []).map((network) => ({
        id: network.networkId,
        label: network.companyName,
        values: { searches: num(network.searches) },
        children: (network.agents ?? []).map((agent) => ({
          id: agent.agentId,
          label: agent.name,
          sublabel: agent.email || null,
          values: { searches: num(agent.searches) },
          children: (agent.properties ?? []).map((prop) => ({
            id: prop.propertyId,
            label: prop.address,
            values: { searches: num(prop.searches) },
          })),
        })),
      })),
    [data],
  );

  const monthlyPricingSlot = (
    <PlatformMonthlyPricing
      provider={provider}
      monthlyPrices={monthlyPrices}
      dateValue={dateValue}
      onSetMonthlyPrice={onSetMonthlyPrice}
    />
  );

  return (
    <CostBreakdownModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="Network → agent → property"
      summary={[{ label: "Searches", value: num(totalSearches) }]}
      headerSlot={monthlyPricingSlot}
      views={[
        {
          key: "network",
          label: "By network",
          levels: [
            { header: "Network", columns: registryCols },
            { header: "Agent", columns: registryCols },
            { header: "Property", columns: registryCols },
          ],
          nodes,
          emptyText: "No searches recorded for this period.",
        },
      ]}
    />
  );
};
